/**
 * Everything the visitor arrived with: the office slug from the path, an
 * identity token and a preselected visit type from a staff link (OB-09), and
 * attribution from UTM parameters, a click id or the referrer (OB-21). Pure;
 * the caller strips the token from the address bar afterwards.
 */
import type { Attribution } from '@velora/contracts/booking';

export interface Entry {
  slug: string | null;
  token: string | null;
  typeId: string | null;
  attribution: Attribution;
}

const SLUG = /^[a-z0-9][a-z0-9-]{2,62}$/;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const CHANNELS = ['web', 'google', 'chat', 'voice', 'sms_link', 'qr', 'email'] as const;
type Channel = (typeof CHANNELS)[number];

const clip = (v: string | null, max: number): string | undefined => {
  const t = v?.trim();
  return t ? t.slice(0, max) : undefined;
};

/** `/b/<slug>` (also tolerates a trailing slash or `/b/<slug>/anything`). */
export function slugFromPath(pathname: string): string | null {
  const m = /^\/b\/([^/?#]+)/.exec(pathname);
  const s = m?.[1]?.toLowerCase() ?? null;
  return s && SLUG.test(s) ? s : null;
}

function referrerChannel(referrer: string, ownHost: string): Partial<Attribution> {
  if (!referrer) return {};
  let host: string;
  try {
    host = new URL(referrer).hostname.toLowerCase();
  } catch {
    return {};
  }
  if (!host || host === ownHost) return {};
  if (/(^|\.)google\.[a-z.]+$/.test(host)) {
    return { channel: 'google', source: 'google', medium: 'organic', referrer: host };
  }
  if (/(^|\.)(bing|duckduckgo|yahoo)\.[a-z.]+$/.test(host)) {
    return { source: host.split('.').slice(-2, -1)[0] ?? host, medium: 'organic', referrer: host };
  }
  if (/(^|\.)(facebook|instagram|fb)\.(com|me)$/.test(host)) {
    return { source: 'facebook', medium: 'social', referrer: host };
  }
  return { source: host.replace(/^www\./, ''), medium: 'referral', referrer: host };
}

export function parseEntry(href: string, referrer = ''): Entry {
  const url = new URL(href);
  const q = url.searchParams;
  const rawToken = q.get('t');
  const token = rawToken && /^[A-Za-z0-9_-]{20,128}$/.test(rawToken) ? rawToken : null;
  const rawType = q.get('type');
  const typeId = rawType && UUID.test(rawType) ? rawType.toLowerCase() : null;

  const a: Attribution = {};
  const source = clip(q.get('utm_source'), 80);
  const medium = clip(q.get('utm_medium'), 80);
  const campaign = clip(q.get('utm_campaign'), 120);
  const term = clip(q.get('utm_term'), 120);
  const content = clip(q.get('utm_content'), 120);
  if (source) a.source = source;
  if (medium) a.medium = medium;
  if (campaign) a.campaign = campaign;
  if (term) a.term = term;
  if (content) a.content = content;

  const ch = q.get('channel')?.toLowerCase();
  if (ch && (CHANNELS as readonly string[]).includes(ch)) a.channel = ch as Channel;
  else if (medium === 'sms') a.channel = 'sms_link';
  else if (medium === 'email') a.channel = 'email';
  else if (medium === 'qr' || source === 'qr') a.channel = 'qr';
  else if (source === 'gbp' || source === 'google_business') a.channel = 'google';

  // Ad click ids without UTM tags still say where the visit came from.
  if (!a.source && q.get('gclid')) Object.assign(a, { source: 'google', medium: 'cpc' });
  if (!a.source && q.get('fbclid')) Object.assign(a, { source: 'facebook', medium: 'social' });

  if (!a.source && !a.channel) {
    const r = referrerChannel(referrer, url.hostname.toLowerCase());
    Object.assign(a, r);
  } else if (referrer) {
    try {
      const host = new URL(referrer).hostname.toLowerCase();
      if (host && host !== url.hostname.toLowerCase()) a.referrer = host.slice(0, 300);
    } catch {
      /* not a URL */
    }
  }

  return { slug: slugFromPath(url.pathname), token, typeId, attribution: a };
}

/**
 * True when we already know where the visitor came from, so "How did you
 * hear about us?" is not asked (OB-21).
 */
export function knowsSource(a: Attribution): boolean {
  return Boolean(a.source || a.campaign || (a.channel && a.channel !== 'web'));
}

/** The address bar without the one-time token (keeps UTM and type for reloads). */
export function withoutToken(href: string): string {
  const url = new URL(href);
  url.searchParams.delete('t');
  return url.pathname + (url.search ? url.search : '') + url.hash;
}
